import AttendanceHistoryTable from "./AttendanceHistoryTable";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { useCallback } from "react";
import Search from "src/components/input/Search";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { toast } from "react-toastify";
import { DatePicker } from "src/components/ui/DatePicker";
import moment from "moment";
import { GenericPagination } from "src/components/ui/pagination";
import { Trash, Upload } from "lucide-react";
import { useAttendanceHistory } from "./hooks/useAttendanceHistory";
import GetinExcelModal from "./GetinExcelModal";


const useAttendanceSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const employee_code = searchParams.get("employee_code") ?? "";
  const name = searchParams.get("name") ?? "";
  const date = searchParams.get("date") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));

  const setParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value !== undefined && value !== "") next.set(key, value);
          else next.delete(key);
        });
        return next;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (next: number) => setParams({ page: next.toString() }),
    [setParams],
  );

  const clearAll = useCallback(() => setSearchParams({}), [setSearchParams]);

  return { name, employee_code, date, page, setParams, setPage, clearAll };
};


const AttendanceHistoryPage = () => {
  const { name, employee_code, date, page, setParams, setPage, clearAll } =
    useAttendanceSearchParams();

  const [debouncedEmployeeCode] = useDebounce(employee_code, 800);
  const [debouncedname] = useDebounce(name, 800);
  const [debouncedDate] = useDebounce(date, 500);

  const pagesize = 25;

  const filters = {
    page,
    employee_code: debouncedEmployeeCode,
    name: debouncedname,
    date: debouncedDate,
  }

  const {
    data: attendance = [],
    isLoading,
    isError,
    isFetching,
  } = useAttendanceHistory(filters);

  const hasNextPage = attendance?.length === pagesize;
  const isLastPage = attendance.length === 0;

  const handleEmployeeCodeChange = (value: string) => {
    setParams({ employee_code: value, page: "1" });
  };

  const handleNameChange = (value: string) => {
    setParams({ name: value, page: "1"});
  }

  const handleDateChange = (newDate: Date | undefined) => {
    const formatted = newDate ? moment(newDate).format("YYYY-MM-DD") : "";
    setParams({ date: formatted, page: "1" });
  };

  const handleNextPage = () => {
    if (!hasNextPage || isLastPage) {
      toast.info("You've reached the last page.");
      return;
    }
    setPage(page + 1);
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page.");
      return;
    }
    setPage(page - 1);
  };

  return (
    <div className="pt-6">
      <div className="sticky top-0 backdrop-blur-lg">
        <HRTitleHeader
          title="Attendance History Of Employees"
          info="View Attendance History of Employees"
        />

        <div className="flex justify-between gap-4 mb-6">
          <div className="flex justify-start gap-4">
            <Search
              value={employee_code}
              placeholder="Search by Employee Code"
              onChange={handleEmployeeCodeChange}
            />
            <Search
              value={name}
              placeholder="Search by Employee Name"
              onChange={handleNameChange}
            />
            <DatePicker
              value={date ? new Date(date) : undefined}
              onChange={handleDateChange}
            />
            <button
              type="button"
              onClick={clearAll}
              className="px-4 py-2 h-10 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 transition"
            >
              <Trash />
            </button>
          </div>
          <GetinExcelModal>
            <button className="max-w-md flex gap-2 justify-center items-center px-3 py-1 hover:bg-primary border border-primary text-primary font-medium  hover:text-white rounded-md">
              <Upload size={16} />
              <span>Export PDF</span>
            </button>
          </GetinExcelModal>
        </div>
      </div>

      {isError ? (
        <div className="text-center text-red-500 mt-10">
          Failed to load attendance records. Please try again.
        </div>
      ) : isLoading ? (
        <div className="text-center text-gray-500 mt-10 flex items-center justify-center gap-2">
          <h2>Loading Attendance...</h2>
          <span className="loading loading-dots loading-md" />
        </div>
      ) : (
        <div
          className={
            isFetching
              ? "opacity-60 pointer-events-none transition-opacity"
              : ""
          }
        >
          <AttendanceHistoryTable data={attendance} />
          <div className="flex-grow my-3">
            <GenericPagination
              currentPage={page}
              onNext={handleNextPage}
              onPrev={handlePrevPage}
              jumpPage={setPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceHistoryPage;
